import React, { Dispatch, SetStateAction, useState } from "react";
import { IDocumentsInput } from "../../exports/interfaces";
import { Eye, Plus, Upload } from "lucide-react";
import {
  getFileIcon,
  renderExistingFilePreview,
  renderFilePreview,
  validateFileSize,
} from "../../exports/functions";
import DocumentPopUp from "../DocumentPopUp";
import DeletePopUp from "../DeletePopUp";
import { dynamicFailed } from "../../hooks/useToast";

const Documents = ({
  documents,
  setDocuments,
  errors,
}: {
  documents: IDocumentsInput;
  setDocuments: Dispatch<SetStateAction<IDocumentsInput>>;
  errors: Record<string, string>;
}) => {
  const [docModal, setDocModal] = useState<{
    isOpen: boolean;
    url?: string | null;
    docs?: string | null;
    fileName?: string;
    fileType?: string;
  }>({ isOpen: false });

  const [deleteExistingIndex, setDeleteExistingIndex] = useState<number | null>(
    null,
  );

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    inputFile: string,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!validateFileSize(file)) {
        e.target.value = "";
        return;
      }
      if (inputFile === "regCertificate") {
        setDocuments((prev) => ({
          ...prev,
          regCertificateFileName: file.name,
          regCertificateFile: file,
          regCertificateDocumentUrl: "",
        }));
      } else if (inputFile === "lawDocument") {
        setDocuments((prev) => ({
          ...prev,
          lawDocumentFileName: file.name,
          lawDocumentFile: file,
          lawDocumentDocumentUrl: "",
        }));
      } else if (inputFile === "foundingDocument") {
        setDocuments((prev) => ({
          ...prev,
          foundingDocumentFileName: file.name,
          foundingDocumentFile: file,
          foundingDocumentDocumentUrl: "",
        }));
      } else if (inputFile === "panDocumentFile") {
        setDocuments((prev) => ({
          ...prev,
          panDocumentFileName: file.name,
          panDocumentFile: file,
          panDocumentDocumentUrl: "",
        }));
      } else if (inputFile === "businessRegistrationFile") {
        setDocuments((prev) => ({
          ...prev,
          businessRegistrationFileName: file.name,
          businessRegistrationFile: file,
          businessRegistrationDocumentUrl: "",
        }));
      } else if (inputFile === "taxClearanceFile") {
        setDocuments((prev) => ({
          ...prev,
          taxClearanceFileName: file.name,
          taxClearanceFile: file,
          taxClearanceDocumentUrl: "",
        }));
      } else if (inputFile === "meetingDocument") {
        setDocuments((prev) => ({
          ...prev,
          meetingDocumentFileName: file.name,
          meetingDocumentFile: file,
          meetingDocumentDocumentUrl: "",
        }));
      } else if (inputFile === "agmDocumentFile") {
        setDocuments((prev) => ({
          ...prev,
          agmDocumentFileName: file.name,
          agmDocumentFile: file,
          agmDocumentDocumentUrl: "",
        }));
      }
    }
  };

  const MAX_OTHER_DOCS = 10;

  const currentOtherFiles: File[] =
    documents.otherDocumentFiles && documents.otherDocumentFiles.length > 0
      ? documents.otherDocumentFiles
      : documents.otherDocumentFile
        ? [documents.otherDocumentFile]
        : [];

  const currentOtherUrls: string[] =
    documents.otherDocumentDocumentUrls &&
    documents.otherDocumentDocumentUrls.length > 0
      ? documents.otherDocumentDocumentUrls
      : documents.otherDocumentDocumentUrl
        ? [documents.otherDocumentDocumentUrl]
        : [];

  const totalOtherCount = currentOtherFiles.length + currentOtherUrls.length;

  const handleOtherFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const availableSlots = MAX_OTHER_DOCS - totalOtherCount;
    if (availableSlots <= 0) {
      dynamicFailed(
        `You can upload a maximum of ${MAX_OTHER_DOCS} other supporting documents.`,
      );
      e.target.value = "";
      return;
    }

    const validNewFiles: File[] = [];
    const validNewNames: string[] = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      if (!validateFileSize(file)) {
        continue;
      }
      if (validNewFiles.length >= availableSlots) {
        dynamicFailed(
          `Maximum ${MAX_OTHER_DOCS} other supporting documents allowed.`,
        );
        break;
      }
      validNewFiles.push(file);
      validNewNames.push(file.name);
    }

    if (validNewFiles.length > 0) {
      setDocuments((prev) => {
        const existingF =
          prev.otherDocumentFiles && prev.otherDocumentFiles.length > 0
            ? prev.otherDocumentFiles
            : prev.otherDocumentFile
              ? [prev.otherDocumentFile]
              : [];
        const existingN =
          prev.otherDocumentFileNames && prev.otherDocumentFileNames.length > 0
            ? prev.otherDocumentFileNames
            : prev.otherDocumentFileName
              ? [prev.otherDocumentFileName]
              : [];

        const updatedFiles = [...existingF, ...validNewFiles];
        const updatedNames = [...existingN, ...validNewNames];

        return {
          ...prev,
          otherDocumentFiles: updatedFiles,
          otherDocumentFileNames: updatedNames,
          otherDocumentFile: updatedFiles[0] || null,
          otherDocumentFileName: updatedNames[0] || "",
        };
      });
    }

    e.target.value = "";
  };

  const handleRemoveOtherFile = (index: number) => {
    setDocuments((prev) => {
      const existingF =
        prev.otherDocumentFiles && prev.otherDocumentFiles.length > 0
          ? prev.otherDocumentFiles
          : prev.otherDocumentFile
            ? [prev.otherDocumentFile]
            : [];
      const existingN =
        prev.otherDocumentFileNames && prev.otherDocumentFileNames.length > 0
          ? prev.otherDocumentFileNames
          : prev.otherDocumentFileName
            ? [prev.otherDocumentFileName]
            : [];

      const updatedFiles = existingF.filter((_, i) => i !== index);
      const updatedNames = existingN.filter((_, i) => i !== index);

      return {
        ...prev,
        otherDocumentFiles: updatedFiles,
        otherDocumentFileNames: updatedNames,
        otherDocumentFile: updatedFiles[0] || null,
        otherDocumentFileName: updatedNames[0] || "",
      };
    });
  };

  const handleRemoveExistingOtherUrl = (index: number) => {
    setDocuments((prev) => {
      const existingUrls =
        prev.otherDocumentDocumentUrls &&
        prev.otherDocumentDocumentUrls.length > 0
          ? prev.otherDocumentDocumentUrls
          : prev.otherDocumentDocumentUrl
            ? [prev.otherDocumentDocumentUrl]
            : [];

      const updatedUrls = existingUrls.filter((_, i) => i !== index);

      return {
        ...prev,
        otherDocumentDocumentUrls: updatedUrls,
        otherDocumentDocumentUrl: updatedUrls[0] || "",
      };
    });
  };

  const isImageFile = (filename?: string, type?: string) => {
    if (type && type.startsWith("image/")) return true;
    const ext = (filename || "").split(".").pop()?.toLowerCase();
    return ["jpg", "jpeg", "png", "webp", "gif"].includes(ext || "");
  };

  const isPdfFile = (filename?: string, type?: string) => {
    if (type === "application/pdf") return true;
    const ext = (filename || "").split(".").pop()?.toLowerCase();
    return ext === "pdf";
  };

  return (
    <>
      <div className={`dashboard-card mt-2`}>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h6>Registration Documents</h6>
            <small>
              Registration certificate, bylaws, and founding documents
            </small>
          </div>
        </div>
        <div className="row gx-5 gy-4">
          <div className="col-12">
            <label className="form-label">
              Upload Registration Certificate (PDF, JPEG, PNG)
            </label>
            <div
              className={`upload-box p-4 ${
                errors.regCertificateFile ? "input-error" : ""
              } ${
                !documents.regCertificateFile &&
                !documents.regCertificateDocumentUrl &&
                "text-center"
              }`}
            >
              {documents.regCertificateFile ? (
                renderFilePreview(
                  documents.regCertificateFile,
                  documents.regCertificateFileName,
                  (e: any) => handleFileChange(e, "regCertificate"),
                )
              ) : documents.regCertificateDocumentUrl ? (
                renderExistingFilePreview(
                  documents.regCertificateDocumentUrl,
                  (e: any) => handleFileChange(e, "regCertificate"),
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse File
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e, "regCertificate");
                      }}
                    />
                  </label>
                </>
              )}
            </div>
            {errors.regCertificateFile && (
              <p className="error-message">{errors.regCertificateFile}</p>
            )}
          </div>
          <div className="col-12">
            <label className="form-label">
              Upload by Law Document (PDF, JPEG, PNG)
            </label>

            <div
              className={`upload-box p-4 ${
                errors.lawDocumentFile ? "input-error" : ""
              } ${
                !documents.lawDocumentFile &&
                !documents.lawDocumentDocumentUrl &&
                "text-center"
              }`}
            >
              {documents.lawDocumentFile ? (
                renderFilePreview(
                  documents.lawDocumentFile,
                  documents.lawDocumentFileName,
                  (e: any) => handleFileChange(e, "lawDocument"),
                )
              ) : documents.lawDocumentDocumentUrl ? (
                renderExistingFilePreview(
                  documents.lawDocumentDocumentUrl,
                  (e: any) => handleFileChange(e, "lawDocument"),
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse File
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e, "lawDocument");
                      }}
                    />
                  </label>
                </>
              )}
            </div>
            {errors.lawDocumentFile && (
              <p className="error-message">{errors.lawDocumentFile}</p>
            )}
          </div>
          <div className="col-12">
            <label className="form-label">
              Upload Founding Document (PDF, JPEG, PNG)
            </label>

            <div
              className={`upload-box p-4 ${
                errors.foundingDocumentFile ? "input-error" : ""
              } ${
                !documents.foundingDocumentFile &&
                !documents.foundingDocumentDocumentUrl &&
                "text-center"
              }`}
            >
              {documents.foundingDocumentFile ? (
                renderFilePreview(
                  documents.foundingDocumentFile,
                  documents.foundingDocumentFileName,
                  (e: any) => handleFileChange(e, "foundingDocument"),
                )
              ) : documents.foundingDocumentDocumentUrl ? (
                renderExistingFilePreview(
                  documents.foundingDocumentDocumentUrl,
                  (e: any) => handleFileChange(e, "foundingDocument"),
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse File
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e, "foundingDocument");
                      }}
                    />
                  </label>
                </>
              )}
            </div>
            {errors.foundingDocumentFile && (
              <p className="error-message">{errors.foundingDocumentFile}</p>
            )}
          </div>
          <div className="col-12">
            <label className="form-label">
              Upload PAN Registration Document (PDF, JPEG, PNG)
            </label>

            <div
              className={`upload-box p-4 ${
                errors.panDocumentFile ? "input-error" : ""
              } ${
                !documents.panDocumentFile &&
                !documents.panDocumentDocumentUrl &&
                "text-center"
              }`}
            >
              {documents.panDocumentFile ? (
                renderFilePreview(
                  documents.panDocumentFile,
                  documents.panDocumentFileName,
                  (e: any) => handleFileChange(e, "panDocumentFile"),
                )
              ) : documents.panDocumentDocumentUrl ? (
                renderExistingFilePreview(
                  documents.panDocumentDocumentUrl,
                  (e: any) => handleFileChange(e, "panDocumentFile"),
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse File
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e, "panDocumentFile");
                      }}
                    />
                  </label>
                </>
              )}
            </div>
            {errors.panDocumentFile && (
              <p className="error-message">{errors.panDocumentFile}</p>
            )}
          </div>
          <div className="col-12">
            <label className="form-label">
              Upload Business Registration Document (PDF, JPEG, PNG)
            </label>

            <div
              className={`upload-box p-4 ${
                errors.businessRegistrationFile ? "input-error" : ""
              } ${
                !documents.businessRegistrationFile &&
                !documents.businessRegistrationDocumentUrl &&
                "text-center"
              }`}
            >
              {documents.businessRegistrationFile ? (
                renderFilePreview(
                  documents.businessRegistrationFile,
                  documents.businessRegistrationFileName,
                  (e: any) => handleFileChange(e, "businessRegistrationFile"),
                )
              ) : documents.businessRegistrationDocumentUrl ? (
                renderExistingFilePreview(
                  documents.businessRegistrationDocumentUrl,
                  (e: any) => handleFileChange(e, "businessRegistrationFile"),
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse File
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e, "businessRegistrationFile");
                      }}
                    />
                  </label>
                </>
              )}
            </div>
            {errors.businessRegistrationFile && (
              <p className="error-message">{errors.businessRegistrationFile}</p>
            )}
          </div>
          <div className="col-12">
            <label className="form-label">
              Upload Tax Clearance Document (PDF, JPEG, PNG)
            </label>

            <div
              className={`upload-box p-4 ${
                errors.taxClearanceFile ? "input-error" : ""
              } ${
                !documents.taxClearanceFile &&
                !documents.taxClearanceDocumentUrl &&
                "text-center"
              }`}
            >
              {documents.taxClearanceFile ? (
                renderFilePreview(
                  documents.taxClearanceFile,
                  documents.taxClearanceFileName,
                  (e: any) => handleFileChange(e, "taxClearanceFile"),
                )
              ) : documents.taxClearanceDocumentUrl ? (
                renderExistingFilePreview(
                  documents.taxClearanceDocumentUrl,
                  (e: any) => handleFileChange(e, "taxClearanceFile"),
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse File
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e, "taxClearanceFile");
                      }}
                    />
                  </label>
                </>
              )}
            </div>
            {errors.taxClearanceFile && (
              <p className="error-message">{errors.taxClearanceFile}</p>
            )}
          </div>
        </div>
      </div>

      <div className={`dashboard-card mt-2`}>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h6>Meeting Documents</h6>
            <small>Board meeting minutes, resolutions</small>
          </div>
        </div>
        <div className="row gx-5 gy-4">
          <div className="col-12">
            <label className="form-label">
              Upload Board Meeting Minutes (PDF, JPEG, PNG)
            </label>
            <div
              className={`upload-box p-4 ${
                errors.meetingDocumentFile ? "input-error" : ""
              } ${
                !documents.meetingDocumentFile &&
                !documents.meetingDocumentDocumentUrl &&
                "text-center"
              }`}
            >
              {documents.meetingDocumentFile ? (
                renderFilePreview(
                  documents.meetingDocumentFile,
                  documents.meetingDocumentFileName,
                  (e: any) => handleFileChange(e, "meetingDocument"),
                )
              ) : documents.meetingDocumentDocumentUrl ? (
                renderExistingFilePreview(
                  documents.meetingDocumentDocumentUrl,
                  (e: any) => handleFileChange(e, "meetingDocument"),
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse File
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e, "meetingDocument");
                      }}
                    />
                  </label>
                </>
              )}
            </div>
            {errors.meetingDocumentFile && (
              <p className="error-message">{errors.meetingDocumentFile}</p>
            )}
          </div>
        </div>
      </div>

      <div className={`dashboard-card mt-2`}>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h6>Minutes of the General Meeting</h6>
            <small>Annual General Meeting minutes and reports</small>
          </div>
        </div>
        <div className="row gx-5 gy-4">
          <div className="col-12">
            <label className="form-label">
              Upload Board Meeting Minutes (PDF, JPEG, PNG)
            </label>

            <div
              className={`upload-box p-4 ${
                errors.agmDocumentFile ? "input-error" : ""
              } ${
                !documents.agmDocumentFile &&
                !documents.agmDocumentDocumentUrl &&
                "text-center"
              }`}
            >
              {documents.agmDocumentFile ? (
                renderFilePreview(
                  documents.agmDocumentFile,
                  documents.agmDocumentFileName,
                  (e: any) => handleFileChange(e, "agmDocumentFile"),
                )
              ) : documents.agmDocumentDocumentUrl ? (
                renderExistingFilePreview(
                  documents.agmDocumentDocumentUrl,
                  (e: any) => handleFileChange(e, "agmDocumentFile"),
                )
              ) : (
                <>
                  <Upload />

                  <p className="mb-1">
                    Choose a file or drag and drop it here.
                  </p>

                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse File
                    <input
                      hidden
                      type="file"
                      onChange={(e) => {
                        handleFileChange(e, "agmDocumentFile");
                      }}
                    />
                  </label>
                </>
              )}
            </div>
            {errors.agmDocumentFile && (
              <p className="error-message">{errors.agmDocumentFile}</p>
            )}
          </div>
        </div>
      </div>

      <div className={`dashboard-card mt-2`}>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h6>Other Supporting Documents</h6>
            <small>
              Upload additional supporting documents{" "}
              <small className="fw-bold" style={{ fontSize: "12px" }}>
                (You can upload a maximum of 10 files)
              </small>
            </small>
          </div>
          {totalOtherCount > 0 && (
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-secondary-subtle text-secondary px-3 py-2">
                {totalOtherCount} / 10 Documents
              </span>
            </div>
          )}
        </div>

        <div className="row gx-5 gy-4">
          <div className="col-12">
            <label className="form-label">
              Upload Documents (PDF, JPEG, PNG)
            </label>

            <div
              className={`upload-box p-4 ${
                errors.otherDocumentFile ? "input-error" : ""
              } ${totalOtherCount === 0 && "text-center"}`}
            >
              {totalOtherCount === 0 ? (
                <>
                  <Upload />
                  <p className="mb-1">Choose files or drag and drop here.</p>
                  <small className="d-block mb-3">
                    (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb each, max 10
                    files)
                  </small>

                  <label className="btn btn-badge text-main-color">
                    Browse Files
                    <input
                      hidden
                      type="file"
                      multiple
                      onChange={handleOtherFilesChange}
                    />
                  </label>
                </>
              ) : (
                <div className="d-flex flex-wrap align-items-center gap-4">
                  {/* Add More before every file (up to 5 files total) */}
                  {totalOtherCount < 10 && (
                    <label
                      className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                      style={{
                        width: "200px",
                        height: "200px",
                        border: "2px dashed var(--input-border)",
                        borderRadius: "12px",
                        backgroundColor: "var(--third-color)",
                        cursor: "pointer",
                      }}
                      title="Add More Documents"
                    >
                      <Plus size={32} className="text-main-color mb-2" />
                      <span className="fw-medium text-main-color">
                        Add More
                      </span>
                      <small className="text-muted mt-1">
                        ({totalOtherCount}/10)
                      </small>
                      <input
                        hidden
                        type="file"
                        multiple
                        onChange={handleOtherFilesChange}
                      />
                    </label>
                  )}

                  {/* Existing uploaded URLs (from edit) */}
                  {currentOtherUrls.map((urlPath, index) => {
                    const cleanPath = urlPath.replace(/\\/g, "/");
                    const displayName =
                      cleanPath.split("/").pop() || `Document ${index + 1}`;
                    const isImg = isImageFile(cleanPath);
                    const isPdf = isPdfFile(cleanPath);
                    const fullUrl = `${process.env.REACT_APP_Url}/${cleanPath}`;

                    return (
                      <div
                        key={`existing-${index}`}
                        className="image-preview-wrapper position-relative"
                        title={displayName}
                      >
                        <img
                          src={
                            isImg
                              ? fullUrl
                              : isPdf
                                ? "https://cdn-icons-png.flaticon.com/512/4208/4208479.png"
                                : getFileIcon(cleanPath)
                          }
                          alt="preview"
                          className="file-preview-image"
                        />

                        {/* Trash button in place of edit button - triggers confirmation popup */}
                        <button
                          type="button"
                          className="image-edit-overlay btn btn-badge text-danger px-3 mb-0"
                          onClick={() => setDeleteExistingIndex(index)}
                          title="Delete Document"
                        >
                          <i className="fa-solid fa-trash text-danger"></i>
                        </button>

                        {/* Eye view button */}
                        <button
                          type="button"
                          className="image-view-overlay btn btn-badge text-main-color px-3 mb-0"
                          onClick={() =>
                            setDocModal({
                              isOpen: true,
                              docs: cleanPath,
                              url: fullUrl,
                              fileName: displayName,
                            })
                          }
                          title="View Document"
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                    );
                  })}

                  {/* Newly selected Files */}
                  {currentOtherFiles.map((file, index) => {
                    const isImg = isImageFile(file.name, file.type);
                    const isPdf = isPdfFile(file.name, file.type);
                    const previewUrl = URL.createObjectURL(file);

                    return (
                      <div
                        key={`file-${index}`}
                        className="image-preview-wrapper position-relative"
                        title={file.name}
                      >
                        <img
                          src={
                            isImg
                              ? previewUrl
                              : isPdf
                                ? "https://cdn-icons-png.flaticon.com/512/4208/4208479.png"
                                : getFileIcon(file.name)
                          }
                          alt="preview"
                          className="file-preview-image"
                        />

                        {/* Trash button in place of edit button */}
                        <button
                          type="button"
                          className="image-edit-overlay btn btn-badge text-danger px-3 mb-0"
                          onClick={() => handleRemoveOtherFile(index)}
                          title="Delete Document"
                        >
                          <i className="fa-solid fa-trash text-danger"></i>
                        </button>

                        {/* Eye view button */}
                        <button
                          type="button"
                          className="image-view-overlay btn btn-badge text-main-color px-3 mb-0"
                          onClick={() =>
                            setDocModal({
                              isOpen: true,
                              url: previewUrl,
                              fileName: file.name,
                              fileType: file.type,
                            })
                          }
                          title="View Document"
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {errors.otherDocumentFile && (
              <p className="error-message mt-2">{errors.otherDocumentFile}</p>
            )}
          </div>
        </div>
      </div>

      <DocumentPopUp
        showDoc={docModal.isOpen}
        setShowDoc={(val: any) =>
          setDocModal((prev) => ({
            ...prev,
            isOpen: typeof val === "function" ? val(prev.isOpen) : val,
          }))
        }
        docs={docModal.docs}
        url={docModal.url}
        fileName={docModal.fileName}
        fileType={docModal.fileType}
      />

      {deleteExistingIndex !== null && (
        <DeletePopUp
          onClose={() => setDeleteExistingIndex(null)}
          onConfirm={() => {
            if (deleteExistingIndex !== null) {
              handleRemoveExistingOtherUrl(deleteExistingIndex);
              setDeleteExistingIndex(null);
            }
          }}
          message="This item will be marked for deletion and will be permanently deleted only after you save your changes"
        />
      )}
    </>
  );
};

export default Documents;
